
import { Component, Vue, Prop } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { MobileRaiseProjection, MobileRaiseTemplateProjection } from "@/store/mobile-raise/types";
import Title from "./Title.vue";
import { Group } from "@/store/eventqueue/types";
import { ManualRaiseResult } from "@/store/manual-raise/types";
import getPosition from "@/services/device.position.service";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import NearbyAreas from "@/components/nearby-areas/NearbyAreas.vue";
import { ILatLng } from "@sureview/v2-mapping-saas";
import { AreaNode } from '@/types/sv-data/groups/AreaNode';
import { NearbyArea, NearbyAreasPlatform } from "@/types/sv-data/groups/NearbyArea";

const LocationWaitMs = 15000;

const ManualRaiseStore = namespace("manualRaise");
const EventQueue = namespace("eventqueue");
const mobile = namespace("mobile");
const MobileRaiseTemplates = namespace("mobileRaise");

@Component(
{
	components: {
		"raise-title": Title,
		"area-tree-select": AreaTreeSelect,
		"nearby-areas": NearbyAreas
	}
})
export default class RaiseManualRaise extends Vue
{
	@Getter("getFeature") private getFeature: (featureName: string[]) => boolean;
	@Prop()
	private raise: MobileRaiseProjection;

	@EventQueue.Action private fetchUserZone: () => Promise<void>;
	@EventQueue.Getter private getUserZone: Group;
	@ManualRaiseStore.Action private raiseManualAlarm: ({ payload, autoHandle, raiseAndJoin, templateId }) => Promise<void>;
	@ManualRaiseStore.Mutation private setOnCompleteHandler: (handler: (result: ManualRaiseResult) => void) => void;
	@MobileRaiseTemplates.State(state => state.templateProjections) private templates: MobileRaiseTemplateProjection[];
	@MobileRaiseTemplates.State(state => state.currentTemplateProjectionIdx) private templateIdx: number | null;

	@mobile.Getter("getCurrentLocationRegionGroupID") private regionGroupID: number;
	@mobile.Getter("getWatchedDeviceLocation") private getWatchedDeviceLocation: (timeoutMs: number) => Promise<google.maps.LatLng>;
	private dataLoaded: boolean = false;
	private createdEventId: number | null = null;
	private selectedGroup: AreaNode = null;
	private raisingAlarm: boolean = false;
	private nearbyLocation: { lat: number; lng: number } | null = null;
	private readonly nearbyAreasPlatform = NearbyAreasPlatform.Mobile;
	private locationUnavailable: boolean = false;

	public $refs!: {
		areaTree: AreaTreeSelect,
	};

	private async created(): Promise<void> {
		this.onCreate();
	}

	private async onCreate(): Promise<void> {
		this.setOnCompleteHandler(this.onRaiseFinished.bind(this));
		await this.fetchUserZone();
		this.dataLoaded = true;

		if (this.isNearbyAreasEnabled) {
			this.resolveNearbyLocation();
		}
	}

	// One-shot snapshot on entry (a mobile raise is point-in-time — no movement tracking). Uses the
	// store getter, not getPosition(), which hangs while a watcher is registered.
	private async resolveNearbyLocation(): Promise<void> {
		try {
			const loc = await this.getWatchedDeviceLocation(LocationWaitMs);
			this.nearbyLocation = { lat: loc.lat(), lng: loc.lng() };
		} catch {
			this.locationUnavailable = true;
		}
	}

	private get nearbyAwaitingText(): string {
		return this.locationUnavailable
			? "Location unavailable — search all areas above"
			: "Getting your location…";
	}

	private onNearbyAreaSelected(area: NearbyArea): void {
		this.selectedGroup = { id: area.groupId, label: area.name };
		// Match picking an area from the tree, which closes the dropdown on select.
		this.$refs.areaTree?.closeNearbyAreasDropdown();
	}

	private beforeDestroy(): void {
		this.setOnCompleteHandler(null);
	}

	private onRaiseFinished(result: ManualRaiseResult): void {
		this.createdEventId = result.eventID;
	}

	private get shouldManualRaiseAndJoin(): boolean {
		return this.getFeature(["Mobile", "MobileRaise", "AutoShare"]);
	}

	private get isManualRaiseToAreaEnabled(): boolean {
		return this.getFeature(["Mobile", "MobileRaise", "RaiseToArea"]);
	}

	private get isNearbyAreasEnabled(): boolean {
		return this.getFeature(["Mobile", "MobileRaise", "RaiseToArea", "NearbyAreas"]);
	}

	private get isAreaRegionsEnabled(): boolean {
		return this.getFeature(["Areas", "Regions"]);
	}

	private mounted(): void {
		if(this.isAreaRegionsEnabled && this.regionGroupID) {
			this.selectedGroup = {
				id: this.regionGroupID,
				label: null //Label will be retrieved from the Area Tree Select
			};
		}
	}

	private async onConfirm(): Promise<void> {
		if(this.raisingAlarm) {
			return;
		}

		this.raisingAlarm = true;
		try {
			let posString;
			try {
				const pos: ILatLng = await getPosition(4000);
				posString = `${pos.lat} ${pos.lng}`;
			}
			catch {
				posString = "";
			}

			const payload = {
				serverTypeEvent: this.raise.typeID,
				groupID: this.selectedGroup ? this.selectedGroup.id : this.getUserZone.groupID,
				location: posString
			};
			await this.raiseManualAlarm({ payload, autoHandle: false, raiseAndJoin: this.shouldManualRaiseAndJoin, templateId: this.templates[this.templateIdx].mobileRaiseTemplateID });
			this.$emit("confirm", this.createdEventId);
		}
		catch(ex) {
			throw ex;
		}
		finally {
			this.raisingAlarm = false;
		}
	}

	private onCancel(): void {
		this.$emit('cancel');
	}
}
